import React from 'react';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Box, Button, Flex, Spinner, Text, TextField } from '@radix-ui/themes';

import Sidebar from 'Components/employersSide/Sidebar';
import { logOutIfTokenExpired } from 'Components/helpers';
import { IconEast, IconWest, SearchIcon } from 'shared/icons';
import { useJobsBrowser } from './jobs-browser.provider';
import { JobsBrowserVacancies } from './jobs-browser-vacancies';
import './jobs-browser.scss';

function JobsBrowserView() {
   const navigate = useNavigate();
   const { t } = useTranslation('global');
   const jobsBrowserStore = useJobsBrowser();
   const searchReadyRef = React.useRef(false);

   const handleError = React.useCallback(
      (message: string) => {
         Swal.fire({
            icon: 'error',
            text: message,
            confirmButtonText: t('employerManageJobs.close'),
            title: t('employerFavouriteListProfile.addFavFailedTitle'),
         });
      },
      [t]
   );

   const handleAuthError = React.useCallback(() => {
      navigate('/');
      logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
   }, [navigate, t]);

   React.useEffect(() => {
      const userType = Cookies.get('user_type');
      const tokenKey = Cookies.get('tokenClient');

      if (userType === 'candidate' || !tokenKey) {
         navigate('/auth/employer-login');
         return;
      }

      jobsBrowserStore.loadManagers({
         page: 1,
         resetVacancies: true,
         onError: handleError,
         onAuthError: handleAuthError,
      });

      window.scrollTo(0, 0);
   }, [handleAuthError, handleError, jobsBrowserStore, navigate]);

   React.useEffect(() => {
      if (!searchReadyRef.current) {
         searchReadyRef.current = true;
         return;
      }

      const timeoutId = window.setTimeout(() => {
         jobsBrowserStore.submitSearch({
            onError: handleError,
            onAuthError: handleAuthError,
         });
      }, 500);

      return () => {
         window.clearTimeout(timeoutId);
      };
   }, [handleAuthError, handleError, jobsBrowserStore, jobsBrowserStore.searchInput]);

   const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      jobsBrowserStore.submitSearch({
         onError: handleError,
         onAuthError: handleAuthError,
      });
   };

   const handlePrev = () => {
      jobsBrowserStore.onPrev({
         onError: handleError,
         onAuthError: handleAuthError,
      });
      window.scrollTo(0, 0);
   };

   const handleNext = () => {
      jobsBrowserStore.onNext({
         onError: handleError,
         onAuthError: handleAuthError,
      });
      window.scrollTo(0, 0);
   };

   return (
      <div className="container manageJob jobsBrowser">
         <div className="row">
            <div className="col-lg-3">
               <Sidebar />
            </div>

            <div className="col-lg-9 mb-5">
               <div className="MJHeader PageHeader">
                  <div className="TopHaddingTitle">
                     <Flex justify="between" align="center" gap="3" wrap="wrap">
                        <div className="TopHaddingTitleSec1 jobsBrowserHeaderTitle">
                           <h3>
                              <i>
                                 <img src="/Images/employerSide/icon2color.png" alt="" />
                              </i>
                              <span>{t('employerManageJobs.manageJobs')}</span>
                           </h3>
                        </div>

                        <Link to="/user/createjob">
                           <Button
                              className="jobsBrowserCreateButton"
                              type="button"
                              size="2"
                              color="blue"
                              highContrast
                           >
                              {t('employerManageJobs.createJob')}
                           </Button>
                        </Link>
                     </Flex>

                     <Box mt="5">
                        <form onSubmit={handleSubmit}>
                           <Flex direction="column" gap="3">
                              <Text className="jobsBrowserSearchLabel" size="3">
                                 {t('employerManageJobs.searchTitle')}
                              </Text>
                              <TextField.Root
                                 className="jobsBrowserSearchInput"
                                 id="searchQuery"
                                 name="searchQuery"
                                 value={jobsBrowserStore.searchInput}
                                 onChange={(event) => {
                                    jobsBrowserStore.setSearchInput(event.target.value);
                                 }}
                                 placeholder={t('employerManageJobs.search')}
                              >
                                 <TextField.Slot>
                                    <SearchIcon width={18} height={18} />
                                 </TextField.Slot>
                              </TextField.Root>
                           </Flex>
                        </form>
                     </Box>

                     {jobsBrowserStore.loading ? (
                        <Flex justify="center" py="6">
                           <Spinner size="2" />
                        </Flex>
                     ) : (
                        <Box className="MJBody" mt="5">
                           {jobsBrowserStore.managers.map((manager) => {
                              const vacanciesStore = jobsBrowserStore.getVacancyStore(manager);

                              return (
                                 <Box
                                    key={manager.id}
                                    id={`jobs-browser-manager-${manager.id}`}
                                    className="jobsBrowserManager"
                                 >
                                    <Flex align="start" justify="between" gap="3" wrap="wrap">
                                       <Box>
                                          <h2>{manager.full_name}</h2>
                                          <Text className="jobsBrowserManagerRole" as="p" color="gray" mb="0">
                                             {manager.role === 'owner'
                                                ? t('employerManageJobs.owner')
                                                : t('employerManageJobs.manager')}
                                          </Text>
                                       </Box>

                                    </Flex>

                                    <JobsBrowserVacancies
                                       store={vacanciesStore}
                                       searchQuery={jobsBrowserStore.searchQuery}
                                       onError={handleError}
                                       onAuthError={handleAuthError}
                                    />
                                 </Box>
                              );
                           })}
                        </Box>
                     )}

                     <div className="manageJobPagination">
                        <p className="text-muted empPaginationData">
                           {t('employerManageJobs.managersCount')} {jobsBrowserStore.recordStart} -{' '}
                           {jobsBrowserStore.recordEnd} {t('pagination.of')}{' '}
                           {jobsBrowserStore.totalCount}
                        </p>

                        <div className="d-flex justify-content-center empPaginationButton">
                           <Button
                              type="button"
                              className="jobsBrowserPaginationButton me-2"
                              size="2"
                              variant="outline"
                              color="red"
                              disabled={!jobsBrowserStore.canPrev}
                              onClick={handlePrev}
                           >
                              <IconWest width={16} height={16} />
                              {t('pagination.Prev')}
                           </Button>

                           <Button
                              type="button"
                              className="jobsBrowserPaginationButton"
                              size="2"
                              variant="outline"
                              color="red"
                              disabled={!jobsBrowserStore.canNext}
                              onClick={handleNext}
                           >
                              {t('pagination.Next')}
                              <IconEast width={16} height={16} />
                           </Button>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </div>
   );
}

export const JobsBrowser = observer(JobsBrowserView);
